import { useState, useMemo, useCallback, useEffect, useContext } from "react";
import { Box, Stack, Typography, Card, Grid, CardContent } from "@mui/material";
import { useGridApiRef, getGridStringOperators } from "@mui/x-data-grid";
import Chart from "../../components/Chart";
import NodataImg from "../../assets/images/noData.png";
import { useDispatch, useSelector } from "react-redux";
import { IndividualContentApi } from "../../store/analytics/IndividualContentTable";
import { ThemeContext } from "../../context/ThemeContext";
import dayjs from "dayjs";
import CustomGridToolbar from "../../components/CustomGridToolbar";
import apiRoutes from "../../store/apiConfigs";
import BackdropUI from "../../ui/Backdrop";
import DataGridComponent from "../../components/DataGridComponent";
import { AiOutlineBell } from "react-icons/ai";

const getSerialNumber = (params) => {
  const index = params.api.getAllRowIds().indexOf(params.id);
  const pagination = params.api.state?.pagination || {};
  const page = pagination.paginationModel?.page ?? pagination.page ?? 0;
  const pageSize = pagination.paginationModel?.pageSize ?? pagination.pageSize ?? 10;
  return page * pageSize + index + 1;
};

const sundayPollColumns = [
  {
    field: "s_no",
    headerName: "S. No.",
    flex: 1,
    minWidth: 80,
    align: "center",
    headerAlign: "center",
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params) => <Typography>{getSerialNumber(params)}</Typography>
  },
  {
    field: "username__id",
    headerName: "User Id",
    flex: 1, minWidth: 150,
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "username__fullname",
    headerName: "Full Name",
    flex: 1, minWidth: 200,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) =>
        operator.value === "equals" || operator.value === "contains",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "username__email",
    headerName: "Email",
    flex: 1, minWidth: 200,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) =>
        operator.value === "equals" || operator.value === "contains",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "poll__id",
    headerName: "Poll ID",
    flex: 1, minWidth: 200,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) => operator.value === "equals",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "poll__title",
    headerName: "Poll Title",
    flex: 1, minWidth: 200,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) => operator.value === "equals",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "poll__poll_points",
    headerName: "Poll Points",
    flex: 1, minWidth: 200,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) => operator.value === "equals",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "poll__title_hn",
    headerName: "Poll Title",
    flex: 1, minWidth: 200,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) => operator.value === "equals",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "poll__poll_publish_datatime",
    headerName: "Poll Publish Date",
    flex: 1, minWidth: 200,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) => operator.value === "equals",
    ),
    sortable: false,
    disableColumnMenu: true,
    renderCell: (data) => (
      <>
        <Typography sx={{ overflowWrap: "anywhere" }}>
          {data?.row?.poll__poll_publish_datatime &&
            dayjs(data?.row?.poll__poll_publish_datatime).format("YYYY-MM-DD")}
        </Typography>
      </>
    ),
  },
  {
    field: "poll_option_selected",
    headerName: "Poll Option Selected",
    flex: 1, minWidth: 200,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) =>
        operator.value === "equals" || operator.value === "contains",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "is_liked",
    headerName: "Liked",
    flex: 1, minWidth: 200,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) => operator.value === "equals",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
];

const quizColumns = [
  {
    field: "s_no",
    headerName: "S. No.",
    flex: 1, minWidth: 80,
    align: "center",
    headerAlign: "center",
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params) => <Typography>{getSerialNumber(params)}</Typography>
  },
  {
    field: "user_id",
    headerName: "User Id",
    flex: 1, minWidth: 150,
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "fullname",
    headerName: "Full Name",
    flex: 1, minWidth: 200,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) =>
        operator.value === "equals" || operator.value === "contains",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "email",
    headerName: "Email",
    flex: 1, minWidth: 200,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) =>
        operator.value === "equals" || operator.value === "contains",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "quiz_id",
    headerName: "Quiz ID",
    flex: 1, minWidth: 150,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) => operator.value === "equals",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "quiz_title",
    headerName: "Quiz Title",
    flex: 1, minWidth: 200,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) =>
        operator.value === "equals" || operator.value === "contains",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "total_attempted",
    headerName: "Total Attempted",
    flex: 1, minWidth: 200,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) => operator.value === "equals",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "total_correct",
    headerName: "Total Correct",
    flex: 1, minWidth: 200,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) => operator.value === "equals",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "performance_percentage",
    headerName: "Performance Percentage",
    flex: 1, minWidth: 200,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) => operator.value === "equals",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "user_duration",
    headerName: "User Duration",
    flex: 1, minWidth: 200,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) =>
        operator.value === "equals" || operator.value === "contains",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
];

const webinarColumns = [
  {
    field: "s_no",
    headerName: "S. No.",
    flex: 1, minWidth: 80,
    align: "center",
    headerAlign: "center",
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params) => <Typography>{getSerialNumber(params)}</Typography>
  },
  {
    field: "username_id",
    headerName: "User Id",
    flex: 1, minWidth: 150,
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "username__fullname",
    headerName: "Full Name",
    flex: 1, minWidth: 200,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) =>
        operator.value === "equals" || operator.value === "contains",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "username__email",
    headerName: "Email",
    flex: 1, minWidth: 220,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) =>
        operator.value === "equals" || operator.value === "contains",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "username__username",
    headerName: "Username",
    flex: 1, minWidth: 180,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) =>
        operator.value === "equals" || operator.value === "contains",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "liked_video_id__content_name",
    headerName: "Webinar Title",
    flex: 1, minWidth: 250,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) =>
        operator.value === "equals" || operator.value === "contains",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "liked_video_id__date_and_time",
    headerName: "Webinar Date",
    flex: 1, minWidth: 200,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) =>
        operator.value === "equals" || operator.value === "contains",
    ),
    sortable: false,
    disableColumnMenu: true,
    renderCell: (data) => (
      <>
        <Typography sx={{ overflowWrap: "anywhere" }}>
          {data?.row?.liked_video_id__date_and_time &&
            dayjs(data?.row?.liked_video_id__date_and_time).format(
              "YYYY-MM-DD",
            )}
        </Typography>
      </>
    ),
  },
  {
    field: "timestamp",
    headerName: "Timestamp",
    flex: 1, minWidth: 220,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) =>
        operator.value === "equals" || operator.value === "contains",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "is_liked",
    headerName: "Liked",
    flex: 1, minWidth: 120,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) => operator.value === "equals",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "is_watched",
    headerName: "Watched",
    flex: 1, minWidth: 120,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) => operator.value === "equals",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "total_time_spent",
    headerName: "Total Time Spent (s)",
    flex: 1, minWidth: 180,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) => operator.value === "equals",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "channel_code",
    headerName: "Channel Code",
    flex: 1, minWidth: 160,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) =>
        operator.value === "equals" || operator.value === "contains",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
];

const ai_resourcesColumns = [
  {
    field: "s_no",
    headerName: "S. No.",
    flex: 1, minWidth: 80,
    align: "center",
    headerAlign: "center",
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params) => <Typography>{getSerialNumber(params)}</Typography>
  },
  {
    field: "user__fullname",
    headerName: "Full Name",
    flex: 1, minWidth: 200,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) =>
        operator.value === "equals" || operator.value === "contains",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "user__email",
    headerName: "Email",
    flex: 1, minWidth: 220,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) =>
        operator.value === "equals" || operator.value === "contains",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "user__username",
    headerName: "Username",
    flex: 1, minWidth: 180,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) =>
        operator.value === "equals" || operator.value === "contains",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "toolTitle",
    headerName: "AI Tool Title",
    flex: 1, minWidth: 250,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) =>
        operator.value === "equals" || operator.value === "contains",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "title",
    headerName: "AI Resource Title",
    flex: 1, minWidth: 200,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) =>
        operator.value === "equals" || operator.value === "contains",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "time_spent",
    headerName: "Timespend",
    flex: 1, minWidth: 220,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) =>
        operator.value === "equals" || operator.value === "contains",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "role",
    headerName: "Role",
    flex: 1, minWidth: 180,
    filterable: true,
    filterOperators: getGridStringOperators().filter(
      (operator) =>
        operator.value === "equals" || operator.value === "contains",
    ),
    sortable: false,
    disableColumnMenu: true,
  },
];

export default function IndividualContent({
  graphData,
  region,
  state,
  loading,
  type,
  module,
  monthDaily,
  startDate,
  endDate,
  isMaskingEnabled,
  schoolType,
}) {
  const [activevsavgtimespent, setActiveVsAvgTimeSpent] = useState({
    l1: true,
    l2: true,
  });
  const [teacherRegister, setTeacherRegistration] = useState({
    l1: true,
    l2: true,
    l3: true,
    l4: true,
  });
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const [isDownload, setIsDownload] = useState(false);
  const IndividualContentTable = useSelector(
    (state) => state?.IndividualContentTable,
  );
  const {
    result: tableResult,
    loading: tableLoading,
    total,
  } = IndividualContentTable;
  const [queryOptions, setQueryOptions] = useState({});
  const { paginationModel, setPaginationModel } = useContext(ThemeContext);

  const onFilterChange = useCallback((filterModel) => {
    setQueryOptions({ filterModel: { ...filterModel } });
  }, []);
  const toggleTeacherRegistration = useCallback((key) => {
    setTeacherRegistration((prevState) => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  }, []);
  const toggleActiveVsAvgTimeSpent = useCallback((key) => {
    setActiveVsAvgTimeSpent((prevState) => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  }, []);
  const getRowSpacing = useCallback((params) => {
    return {
      top: params.isFirstVisible ? 0 : 5,
      bottom: params.isLastVisible ? 0 : 5,
    };
  }, []);
  const handleSortModelChange = useCallback((sortModel) => {
    setQueryOptions({ sortModel: [...sortModel] });
  }, []);

  const transformGraphData = useMemo(() => {
    const chartData = Array.isArray(graphData?.monthly_by_month)
      ? graphData?.monthly_by_month.map((item) => {
        const dataPoint = { name: item.month || item.name };
        if (module === "quiz") {
          dataPoint["Total Quiz Attempted"] =
            item.quiz_questions_total_attempted || 0;
          dataPoint["Quiz Questions Total Correct"] =
            item.quiz_questions_total_correct || 0;
        }
        if (module === "sunday_polls") {
          dataPoint["Total Poll Participants"] =
            item.total_poll_participants || 0;
          dataPoint["Total Poll"] = item.total_poll || 0;
        }
        if (module === "webinar") {
          dataPoint["Total Webinar Attendees"] =
            item.webinar_total_attendees || 0;
          dataPoint["Webinar Total Registrations"] =
            item.webinar_total_registrations || 0;
        }
        return dataPoint;
      })
      : [];
    return chartData;
  }, [graphData, type, module]);

  useEffect(() => {
    // console.log("isMaskingEnabled",isMaskingEnabled);
    const filterModel = queryOptions?.filterModel;
    const items = filterModel?.items;
    const firstItem = items && items.length > 0 ? items[0] : {};
    const quickFilterValues = filterModel?.quickFilterValues;
    const sortModel = queryOptions?.sortModel;
    let query;
    if (sortModel?.length > 0) {
      query = {
        module_type: module,
        school_type: schoolType,
        page: paginationModel?.page + 1,
        page_size: paginationModel?.pageSize,
        // sort_order: sortModel[0].sort || "desc",
        // field: firstItem.field || "",
        // operator: firstItem.operator || (quickFilterValues && "contains") || "",
        // search:
        //   firstItem.value ||
        //   (quickFilterValues &&
        //     quickFilterValues.toString().replaceAll(",", " ")) ||
        //   "",
      };
    } else {
      query = {
        module_type: module,
        school_type: schoolType,
        page: paginationModel?.page + 1,
        page_size: paginationModel?.pageSize,
        region: region,
        state: state,
        start_date: startDate,
        end_date: endDate,
        // field: firstItem.field || "",
        // sort_order: "desc",
        // operator: firstItem.operator || (quickFilterValues && "contains") || "",
        // search:
        //   firstItem.value ||
        //   (quickFilterValues &&
        //     quickFilterValues.toString().replaceAll(",", " ")) ||
        //   "",
      };
    }
    dispatch(IndividualContentApi(query));
  }, [
    dispatch,
    queryOptions,
    paginationModel,
    region,
    state,
    module,
    startDate,
    endDate,
    isMaskingEnabled,
    schoolType,
  ]);

  const getRowId = (row) => {
    if (row?.id) return row.id;
    if (row?._id) return row._id;

    const moduleRowIdMap = {
      quiz: (row) => (row?.user_id && row?.quiz_id) ? `${row.user_id}-${row.quiz_id}` : null,
      course: (row) => (row?.user_id && row?.quiz_id) ? `${row.user_id}-${row.quiz_id}` : null,
      sunday_polls: (row) => (row?.username__email && row?.poll__id) ? `${row.username__email}-${row.poll__id}` : null,
      webinar: (row) => (row?.username__email && row?.liked_video_id__id) ? `${row.username__email}-${row.liked_video_id__id}` : null,
      ai_resources: (row) => (row?.user__email && row?.id) ? `${row.user__email}-${row.id}` : null,
    };

    const getId = moduleRowIdMap[module];
    const generatedId = getId ? getId(row) : null;

    if (generatedId) return generatedId;

    // Fallback to avoid duplicate IDs during module transition or missing data
    const fallbackId = Object.values(row)
      .filter((val) => typeof val === "string" || typeof val === "number")
      .join("-");

    return fallbackId || Math.random().toString(36).substring(2, 9);
  };

  return (
    <>
      <Stack
        direction="row"
        spacing={1}
        sx={{ my: 2, padding: "10px" }}
        alignItems="center"
        justifyContent={"space-between"}
      >
        <Typography variant="h3">Individual Content</Typography>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ my: 2 }} alignItems="center">
        <Grid container spacing={1}>
          {graphData?.total_data &&
            Object.entries(graphData.total_data)
              .filter(([data]) => {
                if (module === "quiz") {
                  return [
                    "quiz_questions_total_attempted",
                    "quiz_questions_total_correct",
                  ].includes(data);
                }
                if (module === "sunday_polls") {
                  return ["total_poll", "total_poll_participants"].includes(
                    data,
                  );
                }
                if (module === "webinar") {
                  return [
                    "webinar_total_registrations",
                    "webinar_total_attendees",
                  ].includes(data);
                }
                if (module === "ai_resources") {
                  return [
                    "total_generated_artifacts",
                    "unique_AI_users",
                    "avg_artifacts_per_user",
                    "points_earned_from_AI_resources",
                    "avg_timespend_per_user_(in_Hours)",
                    "overall_generation_time_(in_Hours)",
                    "unique_teacher_count",
                    "unique_educator_count",
                    "unique_parent_count"
                  ].includes(data);
                }
                return false;
              })
              .map(([data, value], index) => {
                return (
                  <Grid item xs={6} sm={6} md={6} lg={3} mb={1} key={data}>
                    <Box
                      className="customCard1"
                      sx={{
                        transition: "none !important",
                        "&:hover": {
                          transform: "none !important",
                        },
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Stack>
                          <Typography sx={{ textTransform: "capitalize" }}>
                            {data.replaceAll("_", " ")}
                          </Typography>
                          <Typography variant="h4" fontWeight={600}>
                            {value}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>
                  </Grid>
                );
              })}
        </Grid>
      </Stack>
      {module !== "ai_resources" && (
        <Stack direction="row" spacing={1} sx={{ my: 2 }} alignItems="center">
          <Chart
            type={"line"}
            height={500}
            data={transformGraphData}
            module={"individualContent"}
            lineState={teacherRegister}
          />
        </Stack>
      )}

      <Stack
        direction="row"
        spacing={1}
        sx={{ my: 2, padding: "10px" }}
        alignItems="center"
      >
        <DataGridComponent
          disableColumnMenu
          disableColumnSorting
          loading={tableLoading}
          rowCount={total}
          filterModel={queryOptions.filterModel}
          pageSize={paginationModel?.pageSize}
          onPageSizeChange={(newPageSize) => {
            setPaginationModel({
              ...paginationModel,
              pageSize: newPageSize,
            });
          }}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sx={{
            height: 500,
          }}
          rows={tableResult || []}
          getRowId={getRowId}
          getRowSpacing={getRowSpacing}
          columns={
            module === "ai_resources"
              ? ai_resourcesColumns
              : module === "quiz"
                ? quizColumns
                : module === "webinar"
                  ? webinarColumns
                  : module === "sunday_polls"
                    ? sundayPollColumns
                    : []
          }
          onSortModelChange={handleSortModelChange}
          onFilterModelChange={onFilterChange}
          slots={{
            noRowsOverlay: () => (
              <Box className="noData">
                <img src={NodataImg} alt="no data found" />
              </Box>
            ),
            toolbar: (props) =>
              CustomGridToolbar({
                ...props,
                moduleType: "IndividualContent",
                isDownload: isDownload,
                setIsDownload: setIsDownload,
                apiUrl: apiRoutes.individualReportCsvExport,
                hasfilter: false,
                hasSearch: false,
                startDate: startDate,
                endDate: endDate,
                region: region,
                state: state,
                additionalQueryParams: {
                  module_type: module,
                  download_csv: true,
                  school_type: schoolType,
                  page: paginationModel?.page + 1,
                  page_size: paginationModel?.pageSize,
                  start_date: startDate,
                  end_date: endDate,
                  region: region,
                  state: state,
                  frequency: monthDaily,
                },
              }),
          }}
          slotProps={{
            loadingOverlay: {
              variant: "linear-progress",
              noRowsVariant: "linear-progress",
            },
            toolbar: {
              showQuickFilter: true,
            },
          }}
        />
      </Stack>
      <BackdropUI open={isDownload} />
    </>
  );
}
